import { FC, useEffect } from "react"
import { Alert, Box, Button, CircularProgress, Stack } from "@mui/material"
import { useNavigate } from "react-router-dom"
import { Userbot } from "../../interfaces"
import { useChainReportQuery } from "../../store"
import { ActiveChainReport } from "./activeChainReport"
import { getApiErrorMessage } from "@/core/utils"

export const isLiveReportEmpty = (
  report: Userbot.ActiveReport | undefined,
): boolean =>
  !report ||
  (!report.name &&
    !report.clientId &&
    report.steps.length === 0 &&
    report.completed === 0 &&
    report.failed === 0 &&
    report.skipped === 0)

interface IProps {
  id: string
}

/**
 * Резолвер отчёта цепочки (SPEC §4.4):
 * 1. Проверяет live-состояние (Redis).
 * 2. Только отсутствующий live-отчёт (404/пустой скелет) ведёт к сохранённому.
 * Нулевые счётчики сами по себе не означают завершения отложенной цепочки.
 * 3. Иначе — показывает live-отчёт.
 */
export const ChainReportResolver: FC<IProps> = ({ id }) => {
  const navigate = useNavigate()
  const { data, isFetching, isError, error, refetch } = useChainReportQuery(
    { id },
    { pollingInterval: 60000 },
  )

  useEffect(() => {
    if (!isFetching) {
      if (
        (isError && (error as { status?: number })?.status === 404) ||
        (!isError && data && isLiveReportEmpty(data))
      ) {
        navigate(`/userbot/chains/finished/${id}`, { replace: true })
      }
    }
  }, [data, isFetching, isError, error, id, navigate])

  if (isFetching && !data) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Stack spacing={2}>
      {isError && (
        <>
          <Alert severity="error">{getApiErrorMessage(error)}</Alert>
          <Button onClick={() => refetch()}>Обновить</Button>
        </>
      )}
      {data && !isLiveReportEmpty(data) && (
        <ActiveChainReport
          chainId={id}
          onClose={() => navigate("/userbot/chains")}
        />
      )}
    </Stack>
  )
}
